/**
 * Hausje Saved / Je Hausje V2 — local lists, no login.
 * Storage key stays hausje:saved-products:v1.
 * V1 items are kept and shown in the default list "Mijn vondsten".
 */
import {
  DEFAULT_LIST_ID,
  addToList,
  createList,
  deleteList,
  ensureShare,
  isSaved,
  migrationShouldPersist,
  normalizeHandle,
  parseStore,
  previewHandles,
  publicListBody,
  removeFromAll,
  removeFromList,
  renameList,
  setMembership,
  sharedTokenFromSearch,
} from './hausje-lists-model.js';

const STORAGE_KEY = 'hausje:saved-products:v1';
const EVENT_NAME = 'hausje:saved-changed';
const LABEL_SAVE = 'Bewaar in Je Hausje';
const LABEL_REMOVE = 'Verwijder uit Je Hausje';
const STATE_SAVED = 'Bewaard in Je Hausje';
const STATE_UNSAVED = 'Niet bewaard';

function canUseStorage() {
  try {
    const key = '__hausje_saved_test__';
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

const storageAvailable = canUseStorage();

function readStore() {
  if (!storageAvailable) return parseStore(null);
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const store = parseStore(parsed);
    if (migrationShouldPersist(parsed)) writeStore(store);
    return store;
  } catch {
    return parseStore(null);
  }
}

function writeStore(store) {
  if (!storageAvailable) return false;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        version: 1,
        migratedV1: true,
        items: store.items,
        lists: store.lists,
      })
    );
    return true;
  } catch {
    return false;
  }
}

function emit(handle, action, items) {
  window.dispatchEvent(
    new CustomEvent(EVENT_NAME, {
      detail: { items, handle: handle || null, action, count: items.length },
    })
  );
}

function getAll() {
  return readStore().items.slice();
}

function count() {
  return readStore().items.length;
}

function has(handle) {
  return isSaved(readStore(), handle);
}

function sharedToken() {
  return sharedTokenFromSearch(location.search);
}

function isSharedView() {
  return sharedToken() !== '';
}

function shareEndpoint() {
  const configured = document.documentElement.getAttribute('data-hausje-share-endpoint');
  if (configured) return configured.replace(/\/$/, '');
  const host = location.hostname;
  if (host === '127.0.0.1' || host === 'localhost') return 'http://127.0.0.1:8811';
  return 'https://hausje-lists.hausje.workers.dev';
}

async function putShare(list) {
  const response = await fetch(`${shareEndpoint()}/v1/lists/${list.shareToken}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ writeKey: list.writeKey, ...publicListBody(list) }),
  });
  if (!response.ok) throw new Error('share_failed');
}

function syncSharedLists(previous, next) {
  next.lists.forEach((list) => {
    if (!list.shareToken || !list.writeKey) return;
    const before = previous.lists.find((item) => item.id === list.id);
    const changed = !before || before.name !== list.name || JSON.stringify(before.items) !== JSON.stringify(list.items);
    if (changed) putShare(list).catch(() => {});
  });
}

function commit(previous, next, handle) {
  writeStore(next);
  const was = isSaved(previous, handle);
  const now = isSaved(next, handle);
  const action = !was && now ? 'add' : was && !now ? 'remove' : 'lists';
  emit(handle, action, next.items.slice());
  syncSharedLists(previous, next);
  return next;
}

function add(handle, id) {
  const key = normalizeHandle(handle);
  if (!key) return getAll();
  const current = readStore();
  if (isSaved(current, key)) return current.items.slice();
  const next = addToList(current, DEFAULT_LIST_ID, { handle: key, id: id ?? null });
  commit(current, next, key);
  return next.items.slice();
}

function remove(handle, options = {}) {
  const key = normalizeHandle(handle);
  if (!key) return getAll();
  const current = readStore();
  if (!isSaved(current, key)) return current.items.slice();
  const next = removeFromAll(current, key);
  writeStore(next);
  if (!options.silent) emit(key, 'remove', next.items.slice());
  syncSharedLists(current, next);
  return next.items.slice();
}

function toggle(handle, id) {
  if (has(handle)) return remove(handle);
  return add(handle, id);
}

function subscribe(listener) {
  if (typeof listener !== 'function') return () => {};
  const handler = (event) => listener(event.detail);
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

function formatMoney(cents, currency) {
  const value = Number(cents);
  if (!Number.isFinite(value)) return '';
  const code = currency || window.Shopify?.currency?.active || 'EUR';
  const locale = document.documentElement.lang || 'nl-NL';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(value / 100);
  } catch {
    return `${(value / 100).toFixed(2)} ${code}`;
  }
}

function productImageUrl(product) {
  const image = product?.featured_image || product?.images?.[0];
  if (!image) return '';
  if (typeof image === 'string') return image.startsWith('//') ? `https:${image}` : image;
  const src = image.src || image;
  if (!src) return '';
  return String(src).startsWith('//') ? `https:${src}` : String(src);
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const HEART_OUTLINE_PATH =
  'M10 5.2393L8.5149 3.77392C6.79996 2.08174 4.01945 2.08174 2.30451 3.77392C0.589562 5.4661 0.589563 8.2097 2.30451 9.90188L10 17.4952L17.6955 9.90188C19.4104 8.2097 19.4104 5.4661 17.6955 3.77392C15.9805 2.08174 13.2 2.08174 11.4851 3.77392L10 5.2393ZM10.765 3.06343C12.8777 0.978857 16.3029 0.978856 18.4155 3.06343C20.5282 5.148 20.5282 8.52779 18.4155 10.6124L10.72 18.2057C10.3224 18.5981 9.67763 18.5981 9.27996 18.2057L1.58446 10.6124C-0.528154 8.52779 -0.528154 5.14801 1.58446 3.06343C3.69708 0.978859 7.12233 0.978858 9.23495 3.06343L10 3.81832L10.765 3.06343Z';
const HEART_FILLED_PATH =
  'M10 17.4952L2.30451 9.90188C0.589563 8.2097 0.589562 5.4661 2.30451 3.77392C4.01945 2.08174 6.79996 2.08174 8.5149 3.77392L10 5.2393L11.4851 3.77392C13.2 2.08174 15.9805 2.08174 17.6955 3.77392C19.4104 5.4661 19.4104 8.2097 17.6955 9.90188L10 17.4952Z';

function saveButtonMarkup(handle, id, saved) {
  const label = saved ? LABEL_REMOVE : LABEL_SAVE;
  const state = saved ? STATE_SAVED : STATE_UNSAVED;
  return `
    <button
      type="button"
      class="hausje-save-button hausje-save-button--card${saved ? ' is-saved' : ''}"
      data-hausje-save
      data-product-handle="${escapeHtml(handle)}"
      data-product-id="${escapeHtml(id || '')}"
      aria-pressed="${saved ? 'true' : 'false'}"
      aria-label="${label}"
    >
      <span class="hausje-save-button__icon" aria-hidden="true">
        <svg class="hausje-save-button__heart hausje-save-button__heart--outline" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor" focusable="false">
          <path d="${HEART_OUTLINE_PATH}" fill-rule="evenodd" />
        </svg>
        <svg class="hausje-save-button__heart hausje-save-button__heart--filled" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor" focusable="false">
          <path d="${HEART_FILLED_PATH}" />
        </svg>
      </span>
      <span class="visually-hidden" data-hausje-save-state>${state}</span>
    </button>
  `;
}

async function fetchProductByHandle(handle) {
  const key = normalizeHandle(handle);
  if (!key) return null;
  try {
    const response = await fetch(`/products/${encodeURIComponent(key)}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

function syncSaveButton(button) {
  const handle = button.getAttribute('data-product-handle');
  if (!handle) return;
  const saved = has(handle);
  button.setAttribute('aria-pressed', saved ? 'true' : 'false');
  button.setAttribute('aria-label', saved ? 'Lijsten voor dit product' : LABEL_SAVE);
  button.classList.toggle('is-saved', saved);
  const state = button.querySelector('[data-hausje-save-state]');
  if (state) {
    const visibleLabel = state.classList.contains('hausje-save-button__label');
    state.textContent = visibleLabel ? (saved ? STATE_SAVED : LABEL_SAVE) : saved ? STATE_SAVED : STATE_UNSAVED;
  }
}

function syncAllSaveButtons(root = document) {
  root.querySelectorAll('[data-hausje-save]').forEach((button) => syncSaveButton(button));
}

function syncNavIndicators() {
  const savedCount = count();
  document.querySelectorAll('[data-hausje-saved-indicator]').forEach((el) => {
    el.setAttribute('data-hausje-has-saved', savedCount > 0 ? 'true' : 'false');
  });
  document.documentElement.setAttribute('data-hausje-saved-count', String(savedCount));
}

function ensurePicker() {
  let dialog = document.querySelector('[data-hausje-list-picker]');
  if (dialog) return dialog;
  dialog = document.createElement('dialog');
  dialog.className = 'hausje-list-picker';
  dialog.dataset.hausjeListPicker = '';
  dialog.innerHTML = `
    <p class="hausje-list-picker__title">Bewaar in een lijst</p>
    <ul class="hausje-list-picker__lists" data-hausje-picker-lists></ul>
    <form class="hausje-list-picker__create" data-hausje-picker-create>
      <label class="visually-hidden" for="hausje-picker-name">Nieuwe lijst</label>
      <input id="hausje-picker-name" class="hausje-list-create__input" name="name" type="text" maxlength="40" placeholder="Nieuwe lijst">
      <button class="hausje-list-create__button" type="submit">Maak</button>
    </form>
    <button class="hausje-list-text hausje-list-picker__done" type="button" data-hausje-picker-close>Klaar</button>
  `;
  document.body.appendChild(dialog);
  dialog.querySelector('[data-hausje-picker-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener('change', (event) => {
    if (isSharedView()) return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'list') return;
    const handle = dialog.dataset.handle;
    const id = dialog.dataset.productId;
    const current = readStore();
    const next = setMembership(current, input.value, { handle, id }, input.checked);
    commit(current, next, handle);
    refreshUi();
  });
  dialog.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-hausje-picker-create]')) return;
    event.preventDefault();
    if (isSharedView()) return;
    const name = new FormData(form).get('name');
    const created = createList(readStore(), name);
    if (!created.list) return;
    const current = created.store;
    const next = addToList(current, created.list.id, { handle: dialog.dataset.handle, id: dialog.dataset.productId });
    commit(current, next, dialog.dataset.handle);
    form.reset();
    fillPicker(dialog);
    refreshUi();
  });
  return dialog;
}

function fillPicker(dialog) {
  const store = readStore();
  const handle = dialog.dataset.handle;
  const list = dialog.querySelector('[data-hausje-picker-lists]');
  list.replaceChildren();
  store.lists.forEach((entry) => {
    const item = document.createElement('li');
    const checked = entry.items.some((product) => product.handle === handle);
    item.className = 'hausje-list-picker__choice';
    item.innerHTML = `
      <input type="checkbox" name="list" value="${escapeHtml(entry.id)}" ${checked ? 'checked' : ''}>
      <span>${escapeHtml(entry.name)}</span>
    `;
    list.appendChild(item);
  });
}

function openPicker(button) {
  if (isSharedView() || button.closest('[data-hausje-share-view]')) return;
  const handle = normalizeHandle(button.getAttribute('data-product-handle'));
  const id = button.getAttribute('data-product-id');
  if (!handle) return;
  let store = readStore();
  if (!isSaved(store, handle)) {
    const next = addToList(store, DEFAULT_LIST_ID, { handle, id });
    commit(store, next, handle);
    store = next;
  }
  const dialog = ensurePicker();
  dialog.dataset.handle = handle;
  dialog.dataset.productId = id || '';
  fillPicker(dialog);
  if (!dialog.open) dialog.showModal();
  refreshUi();
}

function bindSaveButtons(root = document) {
  if (!window.__hausjeSaveDelegated) {
    window.__hausjeSaveDelegated = true;
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const button = target.closest('[data-hausje-save]');
        if (!(button instanceof HTMLElement)) return;
        event.preventDefault();
        event.stopPropagation();
        if (isSharedView() || button.closest('[data-hausje-share-view]')) return;
        openPicker(button);
      },
      true
    );
    document.addEventListener(
      'pointerdown',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('[data-hausje-save]')) event.stopPropagation();
      },
      true
    );
  }
  syncAllSaveButtons(root);
}

function renderSavedCard(product, options = {}) {
  const handle = normalizeHandle(product.handle);
  const href = escapeHtml(product.url || `/products/${handle}`);
  const title = escapeHtml(product.title || handle);
  const shopName = window.Shopify?.shop || '';
  const vendorRaw = product.vendor && product.vendor !== shopName ? product.vendor : '';
  const imageUrl = escapeHtml(productImageUrl(product));
  const priceCents = Number(product.price);
  const showPrice = Number.isFinite(priceCents) && priceCents > 0;
  const price = showPrice ? escapeHtml(formatMoney(priceCents, window.Shopify?.currency?.active)) : '';
  const compare = showPrice && product.compare_at_price > product.price
    ? escapeHtml(formatMoney(product.compare_at_price, window.Shopify?.currency?.active))
    : '';
  const li = document.createElement('li');
  li.className = 'hausje-saved-card';
  li.dataset.productHandle = handle;
  const readOnly = options.readOnly === true;
  li.innerHTML = `
    <div class="hausje-saved-card__media">
      ${readOnly ? '' : saveButtonMarkup(handle, product.id, has(handle))}
      <a class="hausje-saved-card__media-link" href="${href}" aria-label="${title}">
        ${imageUrl ? `<img class="hausje-saved-card__image" src="${imageUrl}" alt="" loading="lazy" width="600" height="600">` : `<span class="hausje-saved-card__placeholder" aria-hidden="true"></span>`}
      </a>
    </div>
    <div class="hausje-saved-card__info">
      ${vendorRaw ? `<p class="hausje-saved-card__vendor">${escapeHtml(vendorRaw)}</p>` : ''}
      <a class="hausje-saved-card__title" href="${href}">${title}</a>
      ${showPrice ? `<p class="hausje-saved-card__price">${compare ? `<s class="hausje-saved-card__compare">${compare}</s>` : ''}<span>${price}</span></p>` : ''}
      ${!readOnly && options.listId ? `<button type="button" class="hausje-list-text hausje-saved-card__remove" data-hausje-list-remove data-product-handle="${escapeHtml(handle)}">Verwijder uit deze lijst</button>` : ''}
    </div>
  `;
  return li;
}

function countLabel(total) {
  return total === 1 ? '1 vondst' : `${total} vondsten`;
}

function collageMarkup(list, covers) {
  const cells = previewHandles(list.items, 4).flatMap((handle) => {
    const src = productImageUrl(covers.get(handle));
    if (!src) return [];
    return [`<span class="hausje-list-card__cell"><img class="hausje-list-card__image" src="${escapeHtml(src)}" alt="" loading="lazy" decoding="async"></span>`];
  });
  if (!cells.length) {
    return `<span class="hausje-list-card__collage" data-collage="0"><span class="hausje-list-card__empty">Nog geen vondsten</span></span>`;
  }
  const shown = Math.min(cells.length, 4);
  return `<span class="hausje-list-card__collage" data-collage="${shown}">${cells.slice(0, 4).join('')}</span>`;
}

function showPanel(page, name) {
  const panels = {
    empty: page.querySelector('[data-hausje-saved-empty]'),
    index: page.querySelector('[data-hausje-list-browser]'),
    detail: page.querySelector('[data-hausje-list-detail]'),
    share: page.querySelector('[data-hausje-share-view]'),
  };
  const shared = name === 'share';
  if (shared) page.dataset.hausjeShared = 'true';
  else delete page.dataset.hausjeShared;
  Object.entries(panels).forEach(([key, element]) => {
    if (!element) return;
    element.hidden = key !== name;
    if (key === 'share') return;
    if (shared) element.setAttribute('inert', '');
    else element.removeAttribute('inert');
  });
  if (shared) document.querySelector('[data-hausje-list-picker]')?.close();
}

function listFromHash(store) {
  const id = new URLSearchParams(location.hash.replace(/^#/, '')).get('lijst');
  if (!id) return null;
  return store.lists.find((list) => list.id === id) || null;
}

let savedPageRenderToken = 0;

async function renderProductGrid(grid, items, listId, options = {}) {
  const products = await Promise.all(items.map((item) => fetchProductByHandle(item.handle)));
  const fragment = document.createDocumentFragment();
  const stale = [];
  products.forEach((product, index) => {
    if (!product) {
      stale.push(items[index].handle);
      return;
    }
    fragment.appendChild(renderSavedCard(product, { listId, readOnly: options.readOnly === true }));
  });
  grid.replaceChildren(fragment);
  return stale;
}

async function renderSavedPage() {
  const page = document.querySelector('[data-hausje-saved-page]');
  if (!page) return;
  const token = ++savedPageRenderToken;
  const shareToken = sharedToken();
  if (shareToken) {
    showPanel(page, 'share');
    const title = page.querySelector('[data-hausje-share-title]');
    const grid = page.querySelector('[data-hausje-share-grid]');
    const error = page.querySelector('[data-hausje-share-error]');
    error.hidden = true;
    try {
      const response = await fetch(`${shareEndpoint()}/v1/lists/${encodeURIComponent(shareToken)}`);
      if (!response.ok) throw new Error('missing');
      const body = await response.json();
      if (token !== savedPageRenderToken) return;
      if (body.writeKey || body.write_key) throw new Error('credential');
      title.textContent = body.name || 'Gedeelde lijst';
      const shareCount = page.querySelector('[data-hausje-share-count]');
      const items = Array.isArray(body.items) ? body.items : [];
      if (shareCount) shareCount.textContent = countLabel(items.length);
      await renderProductGrid(grid, items, null, { readOnly: true });
    } catch {
      if (token !== savedPageRenderToken) return;
      title.textContent = 'Gedeelde lijst';
      const shareCount = page.querySelector('[data-hausje-share-count]');
      if (shareCount) shareCount.textContent = '';
      grid.replaceChildren();
      error.hidden = false;
    }
    return;
  }

  const store = readStore();
  const onlyHome = store.lists.length === 1 && store.lists[0].id === DEFAULT_LIST_ID && store.lists[0].items.length === 0;
  const open = listFromHash(store);
  if (!open && onlyHome) {
    showPanel(page, 'empty');
    return;
  }
  if (!open) {
    showPanel(page, 'index');
    const index = page.querySelector('[data-hausje-list-index]');
    const wanted = [...new Set(store.lists.flatMap((list) => previewHandles(list.items, 4)))];
    const covers = new Map();
    await Promise.all(wanted.map(async (handle) => {
      covers.set(handle, await fetchProductByHandle(handle));
    }));
    if (token !== savedPageRenderToken) return;
    index.replaceChildren();
    store.lists.forEach((list) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hausje-list-card';
      button.dataset.hausjeOpenList = list.id;
      button.innerHTML = `
        ${collageMarkup(list, covers)}
        <span class="hausje-list-card__meta">
          <p class="hausje-list-card__name">${escapeHtml(list.name)}</p>
          <p class="hausje-list-card__count">${countLabel(list.items.length)}</p>
        </span>
      `;
      index.appendChild(button);
    });
    return;
  }

  showPanel(page, 'detail');
  page.dataset.openList = open.id;
  const title = page.querySelector('[data-hausje-list-title]');
  const grid = page.querySelector('[data-hausje-saved-grid]');
  const rename = page.querySelector('[data-hausje-list-rename] input');
  title.textContent = open.name;
  const detailCount = page.querySelector('[data-hausje-list-count]');
  if (detailCount) detailCount.textContent = countLabel(open.items.length);
  if (rename) rename.value = open.name;
  grid.setAttribute('aria-busy', 'true');
  const stale = await renderProductGrid(grid, open.items, open.id);
  if (token !== savedPageRenderToken) return;
  stale.forEach((handle) => {
    const current = readStore();
    const next = removeFromList(current, open.id, handle);
    writeStore(next);
    syncSharedLists(current, next);
  });
  if (stale.length) {
    renderSavedPage();
    return;
  }
  grid.setAttribute('aria-busy', 'false');
  syncAllSaveButtons(grid);
}

function refreshUi() {
  syncAllSaveButtons();
  syncNavIndicators();
  renderSavedPage();
}

function bindPage() {
  const page = document.querySelector('[data-hausje-saved-page]');
  if (!page || page.dataset.hausjeListsBound === 'true') return;
  page.dataset.hausjeListsBound = 'true';
  page.addEventListener('submit', (event) => {
    if (isSharedView()) {
      event.preventDefault();
      return;
    }
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-hausje-list-create]')) {
      event.preventDefault();
      const created = createList(readStore(), new FormData(form).get('name'));
      if (!created.list) return;
      writeStore(created.store);
      form.reset();
      history.pushState(null, '', `#lijst=${encodeURIComponent(created.list.id)}`);
      refreshUi();
    }
    if (form.matches('[data-hausje-list-rename]')) {
      event.preventDefault();
      const id = page.dataset.openList;
      const current = readStore();
      const next = renameList(current, id, new FormData(form).get('name'));
      commit(current, next, '');
      form.hidden = true;
      refreshUi();
    }
  });
  page.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-hausje-share-copy]')) {
      const status = page.querySelector('[data-hausje-share-copy-status]');
      const url = `${location.origin}${location.pathname}?gedeeld=${encodeURIComponent(sharedToken())}`;
      try {
        await navigator.clipboard.writeText(url);
        if (status) status.textContent = 'Link gekopieerd.';
      } catch {
        if (status) status.textContent = url;
      }
      return;
    }
    if (isSharedView()) return;
    const open = target.closest('[data-hausje-open-list]');
    if (open) {
      history.pushState(null, '', `#lijst=${encodeURIComponent(open.dataset.hausjeOpenList)}`);
      refreshUi();
      return;
    }
    if (target.closest('[data-hausje-list-back]')) {
      history.pushState(null, '', location.pathname);
      page.querySelector('[data-hausje-list-delete-confirm]').hidden = true;
      refreshUi();
      return;
    }
    if (target.closest('[data-hausje-list-rename-open]')) {
      const form = page.querySelector('[data-hausje-list-rename]');
      form.hidden = !form.hidden;
      return;
    }
    if (target.closest('[data-hausje-list-delete]')) {
      page.querySelector('[data-hausje-list-delete-confirm]').hidden = false;
      return;
    }
    if (target.closest('[data-hausje-list-delete-yes]')) {
      const current = readStore();
      const next = deleteList(current, page.dataset.openList);
      writeStore(next);
      syncSharedLists(current, next);
      history.pushState(null, '', location.pathname);
      refreshUi();
      return;
    }
    const removeButton = target.closest('[data-hausje-list-remove]');
    if (removeButton) {
      const handle = removeButton.getAttribute('data-product-handle');
      const current = readStore();
      const next = removeFromList(current, page.dataset.openList, handle);
      commit(current, next, handle);
      refreshUi();
      return;
    }
    if (target.closest('[data-hausje-list-share]')) {
      const status = page.querySelector('[data-hausje-list-share-status]');
      const id = page.dataset.openList;
      const current = readStore();
      const next = ensureShare(current, id);
      writeStore(next);
      const list = next.lists.find((entry) => entry.id === id);
      try {
        await putShare(list);
        const url = `${location.origin}/pages/je-hausje?gedeeld=${list.shareToken}`;
        try {
          await navigator.clipboard.writeText(url);
          status.textContent = 'Link gekopieerd. Dezelfde link blijft werken als je de lijst later aanpast.';
        } catch {
          status.textContent = url;
        }
      } catch {
        status.textContent = 'Delen lukt nu niet. Je lijst blijft op dit apparaat.';
      }
    }
  });
}

function init() {
  bindSaveButtons();
  bindPage();
  refreshUi();
  subscribe(() => {
    syncAllSaveButtons();
    syncNavIndicators();
    renderSavedPage();
  });
  window.addEventListener('hashchange', () => renderSavedPage());
  window.addEventListener('popstate', () => renderSavedPage());
  document.addEventListener('shopify:section:load', () => {
    bindSaveButtons();
    bindPage();
    syncAllSaveButtons();
    renderSavedPage();
  });
}

window.HausjeSaved = {
  STORAGE_KEY,
  EVENT_NAME,
  getAll,
  count,
  has,
  add,
  remove,
  toggle,
  subscribe,
  storageAvailable,
  bindSaveButtons,
  syncAllSaveButtons,
};

document.addEventListener('hausje:discovery-appended', (event) => {
  const detailRoot = event?.detail?.root;
  const root = detailRoot instanceof Element ? detailRoot : document;
  bindSaveButtons(root);
  syncAllSaveButtons(root);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
